import { getPartyMemberDisplayName } from "./party-chat";

export type PartyMemberIdentity = {
  username?: string | null;
  name?: string | null;
  email?: string | null;
};

function normalizeIdentity(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function sameIdentity(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const left = normalizeIdentity(a);
  const right = normalizeIdentity(b);
  if (!left || !right) {
    return false;
  }
  return left.toLowerCase() === right.toLowerCase();
}

export function getBallotReminderSenderLabel(
  person: PartyMemberIdentity | null | undefined,
): string | null {
  const name = normalizeIdentity(person?.name);
  const username = normalizeIdentity(person?.username);
  const email = normalizeIdentity(person?.email);

  if (name && username && !sameIdentity(name, username)) {
    return `${name} (${username})`;
  }

  return name ?? username ?? email;
}

export function canViewPartyMemberEmails(input: {
  hideMemberEmails: boolean;
  viewerIsOwner: boolean;
}): boolean {
  return input.viewerIsOwner || !input.hideMemberEmails;
}

export function getPartyParticipantListDisplay(
  user: PartyMemberIdentity,
  options: {
    hideMemberEmails: boolean;
    viewerIsOwner: boolean;
  },
): {
  displayName: string;
  secondaryName: string | null;
  secondaryEmail: string | null;
} {
  const username = normalizeIdentity(user.username);
  const name = normalizeIdentity(user.name);
  const email = normalizeIdentity(user.email);
  const displayName = getPartyMemberDisplayName(user);

  const secondaryName =
    username && name && !sameIdentity(name, username) ? name : null;

  const canViewEmail = canViewPartyMemberEmails(options);
  const secondaryEmail =
    canViewEmail &&
    email &&
    !sameIdentity(email, displayName) &&
    !sameIdentity(email, secondaryName)
      ? email
      : null;

  return { displayName, secondaryName, secondaryEmail };
}

export function redactPartyMemberEmails<T extends { email: string }>(
  user: T,
  canViewEmails: boolean,
): T {
  if (canViewEmails) {
    return user;
  }

  return { ...user, email: "" };
}

export function redactPartyParticipantsForViewer<
  T extends {
    ownerId: string;
    hideMemberEmails: boolean;
    participants: Array<{ user: { email: string } }>;
  },
>(party: T, viewerId: string): T {
  const canView = canViewPartyMemberEmails({
    hideMemberEmails: party.hideMemberEmails,
    viewerIsOwner: party.ownerId === viewerId,
  });

  if (canView) {
    return party;
  }

  return {
    ...party,
    participants: party.participants.map((participant) => ({
      ...participant,
      user: redactPartyMemberEmails(participant.user, false),
    })),
  };
}
